import { useEffect, useRef, useState } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { Spinner } from "@/components/common/Spinner";
import {
	listRunners,
	retryRunnerPrepare,
	revokeRunner,
	updateRunner,
	type DeviceCreator,
	type Runner,
} from "@/lib/api";
import { formatDateTime, formatRelativeDateTime } from "@/lib/format-datetime";
import { robotLabel } from "@/lib/robot";
import {
	RUNNER_LIST_POLL_INTERVAL_MS,
	RUNNER_UPDATE_RESTART_GRACE_MS,
	RUNNER_UPDATE_SLOW_GRACE_MS,
	canRetryRunnerPrepare,
	canRevokeRunner,
	canUpdateRunner,
} from "@/lib/runner";
import { useToastStore } from "@/stores/useToastStore";
import { AddRunnerDialog } from "./AddRunnerDialog";
import { RunnerLicenseCell } from "./RunnerLicenseCell";
import { RunnerStateBadge } from "./RunnerStateBadge";
import { useDeviceLicenseSummary } from "./useDeviceLicenseSummary";

const COLUMNS = [
	"デバイス名",
	"ステータス",
	"ロボット",
	"ライセンス",
	"最終利用",
	"作成者",
	"作成日時",
	"操作",
];

function replaceRunner(runners: Runner[], updated: Runner): Runner[] {
	return runners.map((runner) => (runner.id === updated.id ? updated : runner));
}

function fullName(user: DeviceCreator): string {
	return [user.lastName, user.firstName].filter(Boolean).join(" ");
}

/** サイドバーの「デバイス」のメインビュー。環境を動かすデバイス (ランナー) を管理する。 */
export function RunnersTab() {
	const [runners, setRunners] = useState<Runner[]>([]);
	const [loading, setLoading] = useState(true);
	const [fetching, setFetching] = useState(true);
	const [error, setError] = useState<string | null>(null);
	// 進めると取得中の応答を捨てて取り直す。操作の後にも進め、操作前の応答で行が戻らないようにする。
	const [refreshKey, setRefreshKey] = useState(0);
	const licenses = useDeviceLicenseSummary(refreshKey);
	const [adding, setAdding] = useState(false);
	const [pendingRevoke, setPendingRevoke] = useState<Runner | null>(null);
	const [pendingUpdate, setPendingUpdate] = useState<Runner | null>(null);
	const [retrying, setRetrying] = useState<ReadonlySet<string>>(() => new Set());
	// 更新を送ったランナーの、送ったときの版。一覧の版が変わる (新しい版で起動し直して報告する) までの間は
	// 「更新」を処理中にし、押し直させない。
	const [restarting, setRestarting] = useState<Record<string, string | null>>({});
	const restartTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

	useEffect(() => {
		const timers = restartTimers.current;
		return () => {
			for (const timer of timers.values()) clearTimeout(timer);
		};
	}, []);

	useEffect(() => {
		let cancelled = false;
		let timer: ReturnType<typeof setTimeout> | undefined;
		const load = async () => {
			setFetching(true);
			try {
				const latest = await listRunners();
				if (cancelled) return;
				setRunners(latest);
				setError(null);
			} catch (err) {
				if (!cancelled)
					setError(err instanceof Error ? err.message : "デバイスの取得に失敗しました。");
				throw err;
			} finally {
				if (!cancelled) {
					setLoading(false);
					setFetching(false);
					timer = setTimeout(() => setRefreshKey((key) => key + 1), RUNNER_LIST_POLL_INTERVAL_MS);
				}
			}
		};
		void load().catch((err: unknown) => {
			console.error("デバイスの取得に失敗しました", err);
		});
		return () => {
			cancelled = true;
			clearTimeout(timer);
		};
	}, [refreshKey]);

	const reload = () => setRefreshKey((key) => key + 1);

	const handleRevoke = async () => {
		if (!pendingRevoke) return;
		const revoked = await revokeRunner(pendingRevoke.id);
		setPendingRevoke(null);
		setRunners((current) => replaceRunner(current, revoked));
		reload();
	};

	const awaitRestart = (runner: Runner, graceMs: number) => {
		const id = runner.id;
		setRestarting((current) => ({ ...current, [id]: runner.runnerVersion }));
		clearTimeout(restartTimers.current.get(id));
		restartTimers.current.set(
			id,
			setTimeout(() => {
				restartTimers.current.delete(id);
				setRestarting((current) => {
					const next = { ...current };
					delete next[id];
					return next;
				});
			}, graceMs),
		);
	};

	// 失敗は確認のダイアログに出す。ランナーは結果を返した後に新しい版で起動し直す。
	// Izanagi が結果を待ちきれなかった (504) ときもランナーは更新を続けるので、失敗にせず版が変わるのを待つ。
	const handleUpdate = async () => {
		if (!pendingUpdate) return;
		const runner = pendingUpdate;
		const outcome = await updateRunner(runner.id);
		setPendingUpdate(null);
		if (outcome.timedOut) {
			awaitRestart(runner, RUNNER_UPDATE_SLOW_GRACE_MS);
			useToastStore.getState().show({
				type: "info",
				message: `更新に時間がかかっています。終わると一覧の版が変わります。${RUNNER_UPDATE_SLOW_GRACE_MS / 60_000} 分たっても版が変わらなければ、もう一度「更新」を押すと理由が出ます。`,
			});
		} else {
			awaitRestart(runner, RUNNER_UPDATE_RESTART_GRACE_MS);
			useToastStore.getState().show({
				type: "success",
				message: `ランナーを ${outcome.version} に更新しました。数十秒で新しい版で起動し直します。`,
			});
		}
		reload();
	};

	const handleRetry = async (runner: Runner) => {
		setRetrying((current) => new Set(current).add(runner.id));
		try {
			const result = await retryRunnerPrepare(runner.id);
			setRunners((current) => replaceRunner(current, result.runner));
			if (!result.retried)
				useToastStore.getState().show({
					type: "info",
					message: "環境イメージの取得は進んでいるか終わっているため、やり直しませんでした。",
				});
			reload();
		} catch (err) {
			useToastStore.getState().show({
				type: "error",
				message:
					err instanceof Error ? err.message : "環境イメージの取得のやり直しに失敗しました。",
			});
			throw err;
		} finally {
			setRetrying((current) => {
				const next = new Set(current);
				next.delete(runner.id);
				return next;
			});
		}
	};

	return (
		<div style={{ padding: 32, height: "100%", overflowY: "auto" }} data-testid="devices-tab">
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 12,
					minHeight: 40,
					flexShrink: 0,
					marginBottom: 24,
				}}
			>
				<div style={{ minWidth: 0 }}>
					<h2
						style={{
							fontSize: "var(--text-heading)",
							fontWeight: 600,
							letterSpacing: "var(--tracking-snug)",
							color: "var(--fg-1)",
							margin: 0,
						}}
					>
						デバイス
					</h2>
					<p style={{ color: "var(--fg-2)", fontSize: "var(--text-caption)", margin: "4px 0 0" }}>
						この組織でエージェントを動かすデバイスを管理します。
					</p>
					{licenses.error !== null ? (
						<p
							data-testid="runner-license-summary-error"
							style={{ color: "var(--danger)", fontSize: "var(--text-caption)", margin: "4px 0 0" }}
						>
							{licenses.error}
						</p>
					) : (
						licenses.summary && (
							<p
								data-testid="runner-license-summary"
								style={{
									color: "var(--fg-2)",
									fontSize: "var(--text-caption)",
									fontVariantNumeric: "tabular-nums",
									margin: "4px 0 0",
								}}
							>
								登録 {licenses.summary.registeredCount} 台 / ライセンス{" "}
								{licenses.summary.licenseCount} 台 · 空き{" "}
								<span
									style={{
										color: licenses.summary.freeCount === 0 ? "var(--warning)" : "var(--fg-1)",
									}}
								>
									{licenses.summary.freeCount} 台
								</span>
							</p>
						)
					)}
				</div>
				<ConfirmButton
					label="デバイスを追加"
					icon="plus"
					loading={false}
					onClick={() => setAdding(true)}
					data-testid="runner-create"
				/>
			</div>

			{adding && (
				<AddRunnerDialog
					onRegistered={reload}
					onClose={() => {
						setAdding(false);
						reload();
					}}
				/>
			)}
			<ConfirmDialog
				open={pendingRevoke !== null}
				title="デバイスの登録を解除"
				message={
					pendingRevoke?.name
						? `「${pendingRevoke.name}」の登録を解除しますか？このデバイスのエージェントは使えなくなります。`
						: "このデバイスの登録を解除しますか？このデバイスのエージェントは使えなくなります。"
				}
				confirmLabel="解除する"
				cancelLabel="キャンセル"
				onConfirm={handleRevoke}
				onCancel={() => setPendingRevoke(null)}
			/>
			<ConfirmDialog
				open={pendingUpdate !== null}
				title="ランナーを更新"
				message={`${pendingUpdate?.name ? `「${pendingUpdate.name}」の` : ""}ランナーを Izanagi が配る最新の版に更新します。ランナーが数十秒止まり、開いているターミナルやストリームは切れます (エージェントは動き続けます)。`}
				confirmLabel="更新する"
				cancelLabel="キャンセル"
				confirmVariant="primary"
				onConfirm={handleUpdate}
				onCancel={() => setPendingUpdate(null)}
			/>

			{error && (
				<div
					data-testid="runners-error"
					style={{
						display: "flex",
						alignItems: "center",
						gap: 12,
						padding: "12px 14px",
						marginBottom: 16,
						borderRadius: "var(--radius-3)",
						background: "var(--danger-subtle)",
						boxShadow: "0 0 0 1px var(--danger-line)",
					}}
				>
					<p style={{ color: "var(--danger)", fontSize: "var(--text-ui)", margin: 0 }}>{error}</p>
					<ConfirmButton
						label="再読み込み"
						loading={fetching}
						variant="secondary"
						size="sm"
						onClick={reload}
						data-testid="runners-retry"
					/>
				</div>
			)}

			{(!error || runners.length > 0) && (
				<div
					style={{
						boxShadow: "var(--shadow-ring)",
						borderRadius: "var(--radius-4)",
						overflowX: "auto",
					}}
				>
					<table data-testid="runners-table" style={{ width: "100%", borderCollapse: "collapse" }}>
						<thead>
							<tr style={{ background: "var(--surface-2)" }}>
								{COLUMNS.map((label) => (
									<th
										key={label}
										style={{
											padding: "10px 16px",
											textAlign: "left",
											color: "var(--fg-3)",
											fontSize: "var(--text-caption)",
											fontWeight: 600,
											letterSpacing: "var(--tracking-label)",
											whiteSpace: "nowrap",
											borderBottom: "var(--pane-header-border)",
										}}
									>
										{label}
									</th>
								))}
							</tr>
						</thead>
						<tbody className="select-text">
							{loading ? (
								<tr style={{ height: 52 }}>
									<td
										colSpan={COLUMNS.length}
										style={{ textAlign: "center", verticalAlign: "middle", padding: 16 }}
									>
										<Spinner size={14} />
									</td>
								</tr>
							) : runners.length === 0 ? (
								<tr>
									<td
										colSpan={COLUMNS.length}
										style={{
											padding: "24px 16px",
											color: "var(--fg-3)",
											fontSize: "var(--text-ui)",
											textAlign: "center",
										}}
									>
										デバイスがありません
									</td>
								</tr>
							) : (
								runners.map((runner, index) => (
									<RunnerRow
										key={runner.id}
										runner={runner}
										last={index === runners.length - 1}
										retrying={retrying.has(runner.id)}
										restarting={
											runner.id in restarting && runner.runnerVersion === restarting[runner.id]
										}
										onUpdate={() => setPendingUpdate(runner)}
										onRetry={() => {
											void handleRetry(runner).catch((err: unknown) => {
												console.error("環境イメージの取得のやり直しに失敗しました", err);
											});
										}}
										onRevoke={() => setPendingRevoke(runner)}
									/>
								))
							)}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}

const cellStyle = { padding: "0 16px", color: "var(--fg-2)", fontSize: "var(--text-caption)" };

function RunnerRow({
	runner,
	last,
	retrying,
	restarting,
	onUpdate,
	onRetry,
	onRevoke,
}: {
	runner: Runner;
	last: boolean;
	retrying: boolean;
	/** 更新を送った後、新しい版で起動し直して報告するのを待っている。 */
	restarting: boolean;
	onUpdate: () => void;
	onRetry: () => void;
	onRevoke: () => void;
}) {
	const creatorName = fullName(runner.createdBy);
	const lastSeen = runner.lastSeenAt
		? formatRelativeDateTime(runner.lastSeenAt) || formatDateTime(runner.lastSeenAt) || "—"
		: "未接続";

	return (
		<tr
			data-testid={`runner-row-${runner.id}`}
			style={{ height: 56, borderBottom: last ? "none" : "var(--pane-header-border)" }}
			onMouseEnter={(event) => {
				event.currentTarget.style.background = "var(--surface-2)";
			}}
			onMouseLeave={(event) => {
				event.currentTarget.style.background = "transparent";
			}}
		>
			<td style={{ padding: "0 16px", color: "var(--fg-1)", fontSize: "var(--text-ui)" }}>
				{runner.name || "—"}
			</td>
			<td style={{ padding: "8px 16px" }}>
				<RunnerStateBadge runner={runner} />
			</td>
			<td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
				{runner.robot ? robotLabel(runner.robot) : "—"}
			</td>
			<td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
				<RunnerLicenseCell id={runner.id} license={runner.license} />
			</td>
			<td style={cellStyle}>{lastSeen}</td>
			<td style={cellStyle}>
				<div>{creatorName || runner.createdBy.email || "—"}</div>
				{creatorName && runner.createdBy.email && (
					<div style={{ color: "var(--fg-3)", marginTop: 2 }}>{runner.createdBy.email}</div>
				)}
			</td>
			<td style={cellStyle}>{formatDateTime(runner.createdAt) || "—"}</td>
			<td style={{ padding: "0 16px", whiteSpace: "nowrap" }}>
				<div style={{ display: "flex", gap: 8 }}>
					{canUpdateRunner(runner) && (
						<ConfirmButton
							label="更新"
							loading={restarting}
							variant="primary"
							size="sm"
							onClick={onUpdate}
							data-testid={`runner-update-${runner.id}`}
						/>
					)}
					{canRetryRunnerPrepare(runner) && (
						<ConfirmButton
							label="再試行"
							loading={retrying}
							variant="secondary"
							size="sm"
							onClick={onRetry}
							data-testid={`runner-retry-prepare-${runner.id}`}
						/>
					)}
					{canRevokeRunner(runner) ? (
						<ConfirmButton
							label="解除"
							loading={false}
							variant="destructive"
							size="sm"
							onClick={onRevoke}
							data-testid={`runner-revoke-${runner.id}`}
						/>
					) : (
						<span style={{ color: "var(--fg-3)", fontSize: "var(--text-caption)" }}>—</span>
					)}
				</div>
			</td>
		</tr>
	);
}
